import React, { useState } from 'react';
import { FiLink, FiSearch, FiCheck, FiLoader, FiFile, FiAlertCircle } from 'react-icons/fi';
import { sopSharePointList, sopSharePointSync, sopSyncStatus } from '../../db/company';

export default function SharePointSync() {
  const [path, setPath] = useState('');
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleConnect = async () => {
    if (!path.trim()) return;
    setLoading(true);
    setError(null);
    setFiles([]);
    setSelected(new Set());
    try {
      const data = await sopSharePointList(path.trim());
      if (data?.error) throw new Error(data.error);
      setFiles(data.files || []);
      if (data.files?.length === 0) {
        showToast('No files found at this path', 'error');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFile = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === files.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(files.map(f => f.id)));
    }
  };

  const handleSync = async () => {
    if (selected.size === 0) return;
    setSyncing(true);
    setSyncStatus({ status: 'syncing', processed: 0, total: selected.size });
    try {
      const selectedFiles = files.filter(f => selected.has(f.id));
      const data = await sopSharePointSync(selectedFiles);
      if (data?.error) throw new Error(data.error);
      setSyncStatus({ status: 'complete', ...data });
      showToast(`Synced ${data.synced} file${data.synced !== 1 ? 's' : ''} successfully`);
    } catch (e) {
      setSyncStatus({ status: 'error', error: e.message });
      showToast(`Sync failed: ${e.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const pollSyncStatus = async () => {
    try {
      const data = await sopSyncStatus();
      if (data && !data.error) {
        setSyncStatus(data);
      }
    } catch {
      // ignore poll errors
    }
  };

  // Poll during sync
  React.useEffect(() => {
    if (!syncing) return;
    const interval = setInterval(pollSyncStatus, 2000);
    return () => clearInterval(interval);
  }, [syncing]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 20px', borderRadius: 8,
          background: toast.type === 'error' ? '#DC2626' : '#059669',
          color: '#fff', fontSize: 14, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#F1F5F9', fontSize: 24, fontWeight: 700, margin: 0 }}>
          SharePoint Sync
        </h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '4px 0 0' }}>
          Connect to a SharePoint site to sync SOP documents into the vector database
        </p>
      </div>

      {/* Connect bar */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 24,
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <FiLink size={16} style={{
            position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
            color: '#475569',
          }} />
          <input
            type="text"
            value={path}
            onChange={e => setPath(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConnect()}
            placeholder="SharePoint site URL or drive path (e.g. sites/StrikeSOPs/drives/...)"
            style={{
              width: '100%', padding: '12px 14px 12px 40px',
              background: '#0F1A2E', border: '1px solid #1E2A4A',
              borderRadius: 8, color: '#E2E8F0', fontSize: 14,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          onClick={handleConnect}
          disabled={loading || !path.trim()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 20px', borderRadius: 8,
            background: '#3B82F6', border: 'none',
            color: '#fff', fontSize: 14, fontWeight: 600,
            cursor: loading || !path.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || !path.trim() ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <FiSearch size={16} />}
          Connect
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: '#1C1917', border: '1px solid #DC2626', color: '#FCA5A5', fontSize: 14,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FiAlertCircle size={16} /> {error}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div style={{
          background: '#0F1A2E', borderRadius: 12,
          border: '1px solid #1E2A4A', overflow: 'hidden', marginBottom: 20,
        }}>
          {/* Select all header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: '1px solid #1E2A4A',
          }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10,
              color: '#94A3B8', fontSize: 13, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={selected.size === files.length}
                onChange={toggleAll}
                style={{ accentColor: '#3B82F6' }}
              />
              Select all ({files.length} files)
            </label>
            <span style={{ color: '#475569', fontSize: 12 }}>
              {selected.size} selected
            </span>
          </div>

          {/* File rows */}
          {files.map(file => (
            <div
              key={file.id}
              onClick={() => toggleFile(file.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px',
                borderBottom: '1px solid #1E2A4A',
                cursor: 'pointer',
                background: selected.has(file.id) ? '#111D33' : 'transparent',
                transition: 'background 0.1s',
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(file.id)}
                onChange={() => toggleFile(file.id)}
                onClick={e => e.stopPropagation()}
                style={{ accentColor: '#3B82F6' }}
              />
              <FiFile size={16} style={{ color: '#3B82F6', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: '#E2E8F0', fontSize: 14, fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {file.name}
                </div>
              </div>
              <span style={{ color: '#64748B', fontSize: 12, whiteSpace: 'nowrap' }}>
                {formatSize(file.size)}
              </span>
              <span style={{ color: '#475569', fontSize: 12, whiteSpace: 'nowrap', minWidth: 100 }}>
                {file.lastModified ? new Date(file.lastModified).toLocaleDateString() : '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sync button */}
      {files.length > 0 && (
        <button
          onClick={handleSync}
          disabled={syncing || selected.size === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 8,
            background: selected.size === 0 ? '#1E2A4A' : '#059669',
            border: 'none', color: '#fff', fontSize: 14, fontWeight: 600,
            cursor: syncing || selected.size === 0 ? 'not-allowed' : 'pointer',
            opacity: syncing || selected.size === 0 ? 0.6 : 1,
          }}
        >
          {syncing ? (
            <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <FiCheck size={16} />
          )}
          {syncing ? 'Syncing...' : `Sync Selected (${selected.size})`}
        </button>
      )}

      {/* Sync progress */}
      {syncStatus && syncStatus.status !== 'idle' && (
        <div style={{
          marginTop: 16, padding: '16px',
          background: '#0F1A2E', borderRadius: 12,
          border: '1px solid #1E2A4A',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 8,
          }}>
            <span style={{ color: '#E2E8F0', fontSize: 14, fontWeight: 500 }}>
              {syncStatus.status === 'syncing' ? 'Syncing in progress...' :
               syncStatus.status === 'complete' ? 'Sync complete' :
               'Sync error'}
            </span>
            {syncStatus.total > 0 && (
              <span style={{ color: '#64748B', fontSize: 13 }}>
                {syncStatus.processed || 0} / {syncStatus.total}
              </span>
            )}
          </div>

          {/* Progress bar */}
          {syncStatus.total > 0 && (
            <div style={{
              height: 6, borderRadius: 3, background: '#1E2A4A', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 3,
                background: syncStatus.status === 'error' ? '#DC2626' : '#3B82F6',
                width: `${((syncStatus.processed || 0) / syncStatus.total) * 100}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}

          {syncStatus.current_file && syncStatus.status === 'syncing' && (
            <div style={{ color: '#64748B', fontSize: 12, marginTop: 8 }}>
              Processing: {syncStatus.current_file}
            </div>
          )}

          {/* Results */}
          {syncStatus.results && (
            <div style={{ marginTop: 12 }}>
              {syncStatus.results.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 0', color: r.status === 'ok' ? '#86EFAC' : '#FCA5A5',
                  fontSize: 13,
                }}>
                  {r.status === 'ok' ? <FiCheck size={14} /> : <FiAlertCircle size={14} />}
                  {r.name} — {r.status === 'ok' ? `${r.chunks} chunks` : r.error}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}
